import { useEffect, useRef, useState } from 'react';
import { useAdAnalytics } from '../../hooks/useAdAnalytics';
import { hasAnalyticsConsent } from '../../utils/cookieConsent';

interface AdSenseBannerProps {
  slotId: string;
  format?: 'auto' | 'horizontal' | 'vertical' | 'rectangle';
  /** Show rectangle on mobile, horizontal on desktop */
  responsive?: boolean;
  className?: string;
  /** Callback when ad is detected as blocked/empty */
  onAdBlocked?: () => void;
}

const PUBLISHER_ID = import.meta.env.VITE_ADSENSE_PUBLISHER_ID as string | undefined;
const MAX_ADS_PER_PAGE = 5;

let adsenseScriptLoaded = false;

/** Lazily load the AdSense script only when consent is granted */
function ensureAdSenseScript() {
  if (adsenseScriptLoaded || !PUBLISHER_ID) return;
  adsenseScriptLoaded = true;

  const script = document.createElement('script');
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${PUBLISHER_ID}`;
  script.async = true;
  script.crossOrigin = 'anonymous';
  document.head.appendChild(script);
}

/** Track global ad-blocked state for the detection component */
let adBlockListeners: Array<() => void> = [];
let adBlockDetected = false;

// eslint-disable-next-line react-refresh/only-export-components
export function onAdBlocked(cb: () => void) {
  adBlockListeners.push(cb);
  return () => {
    adBlockListeners = adBlockListeners.filter((l) => l !== cb);
  };
}

// eslint-disable-next-line react-refresh/only-export-components
export function isAdBlocked() {
  return adBlockDetected;
}

function notifyAdBlocked() {
  adBlockDetected = true;
  adBlockListeners.forEach((cb) => cb());
}

/** Track active ad count per page for density warnings */
let activeAdCount = 0;

function useAdDensityTracker() {
  useEffect(() => {
    activeAdCount++;
    if (import.meta.env.DEV && activeAdCount > MAX_ADS_PER_PAGE) {
      console.warn(
        `[AdSense Density] ${activeAdCount} ad units on this page exceeds recommended max of ${MAX_ADS_PER_PAGE}. ` +
          'Google may penalise pages with more ads than content. ' +
          'See: https://support.google.com/adsense/answer/1346295'
      );
    }
    return () => {
      activeAdCount--;
    };
  }, []);
}

/** Check for ?adtest=on URL param (dev/test mode for AdSense) */
function useAdTestMode(): boolean {
  const [testMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('adtest') === 'on';
  });
  return testMode;
}

/** Check for ?addebug=1 URL param for visual debug overlay */
function useAdDebugMode(): boolean {
  const [debugMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('addebug') === '1';
  });
  return debugMode;
}

function useResponsiveFormat(
  responsive: boolean,
  fallback: AdSenseBannerProps['format']
): AdSenseBannerProps['format'] {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    if (!responsive) return;
    const mql = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    setIsMobile(mql.matches);
    return () => mql.removeEventListener('change', handler);
  }, [responsive]);

  if (!responsive) return fallback;
  return isMobile ? 'rectangle' : 'horizontal';
}

export function AdSenseBanner({
  slotId,
  format = 'auto',
  responsive = false,
  className = '',
  onAdBlocked: onAdBlockedProp,
}: AdSenseBannerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hidden, setHidden] = useState(false);
  const pushed = useRef(false);
  const resolvedFormat = useResponsiveFormat(responsive, format);
  const isTestMode = useAdTestMode();
  const isDebugMode = useAdDebugMode();
  const { trackImpression, trackBlocked } = useAdAnalytics();

  useAdDensityTracker();

  // Re-check consent when it changes
  const [consented, setConsented] = useState(hasAnalyticsConsent);

  const handleBlocked = () => {
    setHidden(true);
    notifyAdBlocked();
    trackBlocked(slotId);
    onAdBlockedProp?.();
  };

  useEffect(() => {
    const handler = () => setConsented(hasAnalyticsConsent());
    window.addEventListener('cookie-consent-change', handler);
    return () => window.removeEventListener('cookie-consent-change', handler);
  }, []);

  useEffect(() => {
    if (!PUBLISHER_ID || !consented) {
      setHidden(true);
      return;
    }

    setHidden(false);
    ensureAdSenseScript();

    // Push ad unit after the script has had time to load
    const timer = setTimeout(() => {
      try {
        if (!pushed.current) {
          pushed.current = true;
          const adsbygoogle = (window as unknown as Record<string, unknown>)
            .adsbygoogle as unknown[];
          if (adsbygoogle) {
            adsbygoogle.push({});
            trackImpression(slotId);
          }
        }
      } catch {
        // AdSense not loaded or blocked
        handleBlocked();
      }
    }, 100);

    // Detect empty/blocked ad after 3s
    const detectTimer = setTimeout(() => {
      const el = containerRef.current;
      if (el && el.offsetHeight < 10) {
        handleBlocked();
      }
    }, 3000);

    return () => {
      clearTimeout(timer);
      clearTimeout(detectTimer);
    };
  }, [consented, handleBlocked, slotId, trackBlocked, trackImpression]);

  if (hidden) return null;

  const debugBorder = isDebugMode
    ? 'ring-2 ring-cyan-400 ring-offset-2 ring-offset-gray-900 relative'
    : '';

  return (
    <div ref={containerRef} className={`bg-gray-800 rounded-2xl p-4 ${debugBorder} ${className}`}>
      {isDebugMode && (
        <div className="absolute -top-3 left-3 bg-cyan-500 text-black text-[9px] font-mono font-bold px-2 py-0.5 rounded-full z-10">
          AdSense · slot:{slotId} · fmt:{resolvedFormat} · consent:{consented ? 'yes' : 'no'}
          {isTestMode ? ' · TEST' : ''}
        </div>
      )}
      <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Advertisement</p>
      <ins
        className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={PUBLISHER_ID}
        data-ad-slot={slotId}
        data-ad-format={resolvedFormat}
        data-full-width-responsive="true"
        {...(isTestMode ? { 'data-adtest': 'on' } : {})}
      />
    </div>
  );
}
